/**
 * OAuth 安全工具函数
 * 实现 PKCE (Proof Key for Code Exchange) 和 STATE 验证
 */

import { isDevelopment, getStorageKey as getStorageKeyUtils } from '@/utils'

export const getStorageKey = (type, provider = '') => getStorageKeyUtils(`OAUTH_${type}_${provider}`)

/**
 * Base64 URL 编码（RFC 4648）
 * @param {Uint8Array} buffer - 要编码的字节数组
 * @returns {string} Base64 URL 编码字符串
 */
function base64UrlEncode(buffer) {
  const base64 = btoa(String.fromCharCode(...buffer))
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * 生成随机字符串
 * @param {number} length - 字节长度
 * @returns {string} Base64 URL 编码的随机字符串
 */
function generateRandomString(length = 32) {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return base64UrlEncode(array)
}

// ==================== PKCE 相关函数 ====================

/**
 * 生成 PKCE code_verifier
 * 一个加密随机字符串,作为私钥保存在客户端
 * @returns {string} code_verifier
 */
export function generateVerifier() {
  return generateRandomString(32)
}

/**
 * 生成 PKCE code_challenge
 * 基于 code_verifier 通过 SHA-256 哈希生成
 * @param {string} verifier - code_verifier
 * @returns {Promise<string>} code_challenge
 */
export async function generateChallenge(verifier) {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(new Uint8Array(hash))
}

/**
 * 保存 code_verifier 到 sessionStorage
 * ⚠️ 不要保存到 localStorage,避免 XSS 攻击
 * @param {string} verifier - code_verifier
 * @param {string} provider - OAuth 提供商 ('google' | 'apple')
 */
export function saveVerifier(verifier, provider) {
  const key = getStorageKey('pkce_verifier', provider)
  sessionStorage.setItem(key, verifier)
}

/**
 * 删除 code_verifier
 * @param {string} provider - OAuth 提供商 ('google' | 'apple')
 */
export function removeVerifier(provider) {
  const key = getStorageKey('pkce_verifier', provider)
  sessionStorage.removeItem(key)
}

/**
 * 获取并删除 code_verifier
 * ⚠️ 使用后立即删除,防止重放攻击
 * @param {string} provider - OAuth 提供商 ('google' | 'apple')
 * @returns {string | null} code_verifier
 */
export function getAndRemoveVerifier(provider) {
  const key = getStorageKey('pkce_verifier', provider)
  const verifier = sessionStorage.getItem(key)
  removeVerifier(provider)
  return verifier
}

/**
 * 清除所有 PKCE verifier
 * 用于登出或清理
 */
export function clearAllVerifiers() {
  const keys = Object.keys(sessionStorage).filter(key => key.startsWith(getStorageKey('pkce_verifier')))
  keys.forEach(key => sessionStorage.removeItem(key))
}

// ==================== NONCE 相关函数 ====================

/**
 * 生成 nonce 参数
 * 用于防止重放攻击，绑定 ID Token 和客户端会话
 * @returns {string} nonce
 */
export function generateNonce() {
  return generateRandomString(32)
}

/**
 * 保存 nonce 到 sessionStorage
 * @param {string} nonce - nonce 值
 * @param {string} provider - OAuth 提供商 ('google' | 'apple')
 */
export function saveNonce(nonce, provider) {
  const key = getStorageKey('nonce', provider)
  sessionStorage.setItem(key, nonce)
}

/**
 * 删除 nonce
 * @param {string} provider - OAuth 提供商 ('google' | 'apple')
 */
export function removeNonce(provider) {
  const key = getStorageKey('nonce', provider)
  sessionStorage.removeItem(key)
}

/**
 * 获取但不删除 nonce（用于客户端验证 ID Token）
 * @param {string} provider - OAuth 提供商 ('google' | 'apple')
 * @returns {string | null} nonce
 */
export function getNonce(provider) {
  const key = getStorageKey('nonce', provider)
  return sessionStorage.getItem(key)
}

/**
 * 清除所有 nonce
 * 用于登出或清理
 */
export function clearAllNonces() {
  const keys = Object.keys(sessionStorage).filter(key => key.startsWith(getStorageKey('nonce')))
  keys.forEach(key => sessionStorage.removeItem(key))
}


// ==================== STATE 相关函数 ====================

/**
 * 生成 OAuth state 参数
 * 用于防止 CSRF 攻击
 * @returns {string} state
 */
export function generateState() {
  return generateRandomString(16)
}

/**
 * 保存 state 到 sessionStorage
 * @param {string} state - state 值
 * @param {string} provider - OAuth 提供商 ('google' | 'apple')
 */
export function saveState(state, provider) {
  const key = getStorageKey('state', provider)
  sessionStorage.setItem(key, state)
}

/**
 * 删除 state
 * @param {string} provider - OAuth 提供商 ('google' | 'apple')
 */
export function removeState(provider) {
  const key = getStorageKey('state', provider)
  sessionStorage.removeItem(key)
}

/**
 * 验证并删除 state
 * ⚠️ 验证后立即删除,防止重放攻击
 * @param {string} returnedState - OAuth 回调返回的 state
 * @param {string} provider - OAuth 提供商 ('google' | 'apple')
 * @returns {boolean} 是否验证通过
 */
export function verifyAndRemoveState(returnedState, provider) {
  const key = getStorageKey('state', provider)
  const storedState = sessionStorage.getItem(key)
  removeState(provider)

  // 验证 state 是否匹配
  if (!storedState || storedState !== returnedState) {
    console.error('[OAuth] State validation failed:', {
      stored: storedState,
      returned: returnedState,
      provider
    })
    return false
  }

  return true
}

/**
 * 清除所有 OAuth state
 * 用于登出或清理
 */
export function clearAllStates() {
  const keys = Object.keys(sessionStorage).filter(key => key.startsWith(getStorageKey('state')))
  keys.forEach(key => sessionStorage.removeItem(key))
}

// ==================== 安全检查 ====================

/**
 * 检查浏览器是否支持 Web Crypto API
 * ⚠️ Web Crypto 仅在 HTTPS 或 localhost 环境下支持
 * @returns {boolean}
 */
export function isCryptoSupported() {
  return typeof crypto !== 'undefined' &&
         typeof crypto.subtle !== 'undefined' &&
         typeof crypto.getRandomValues === 'function'
}

/**
 * 检查是否为 HTTPS 环境
 * ⚠️ OAuth 应该只在 HTTPS 环境下使用
 * @returns {boolean}
 */
export function isSecureContext() {
  if (isDevelopment) {
    return true
  }
  return window.isSecureContext
}

/**
 * 验证 OAuth 环境是否安全
 * @throws {Error} 如果环境不安全
 */
export function validateOAuthEnvironment() {
  if (!isCryptoSupported()) {
    throw new Error('Web Crypto API not supported. Please use a modern browser.')
  }

  if (!isSecureContext()) {
    console.warn('[OAuth] Not in HTTPS environment. OAuth should only be used over HTTPS.')
  }
}
